import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GlassCard } from "./GlassCard";
import { 
  Target, 
  Trophy, 
  CheckCircle2, 
  Circle, 
  Flame, 
  Clock,
  ChevronRight,
  Zap,
  Users,
  Crown,
  Calendar,
  Sparkles,
  Play
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { ChallengeActivity } from "./ChallengeActivities";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Challenges that have interactive activities
const INTERACTIVE_CHALLENGES = [
  "deep_breathing",
  "meditate_5min", 
  "hydrate",
  "digital_detox",
  "self_affirmation"
];

// Category colors and icons
const CATEGORY_STYLES = {
  mindfulness: { color: "from-purple-500 to-indigo-500", bg: "bg-purple-500/20", border: "border-purple-500/50", text: "text-purple-400" },
  gratitude: { color: "from-pink-500 to-rose-500", bg: "bg-pink-500/20", border: "border-pink-500/50", text: "text-pink-400" },
  social: { color: "from-cyan-500 to-blue-500", bg: "bg-cyan-500/20", border: "border-cyan-500/50", text: "text-cyan-400" },
  physical: { color: "from-green-500 to-emerald-500", bg: "bg-green-500/20", border: "border-green-500/50", text: "text-green-400" },
  creativity: { color: "from-orange-500 to-amber-500", bg: "bg-orange-500/20", border: "border-orange-500/50", text: "text-orange-400" },
  self_care: { color: "from-neon-cyan to-neon-purple", bg: "bg-neon-cyan/20", border: "border-neon-cyan/50", text: "text-neon-cyan" },
};

const CATEGORY_NAMES = {
  mindfulness: "Mindfulness",
  gratitude: "Gratitud",
  social: "Social",
  physical: "Físico",
  creativity: "Creatividad",
  self_care: "Autocuidado",
};

// Single Challenge Card
const ChallengeCard = ({ challenge, onComplete, onStartActivity, completing }) => {
  const style = CATEGORY_STYLES[challenge.category] || CATEGORY_STYLES.self_care;
  const isCompleted = challenge.completed;
  const hasInteractiveActivity = INTERACTIVE_CHALLENGES.includes(challenge.id);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={!isCompleted ? { scale: 1.02 } : {}}
      className={`relative overflow-hidden rounded-xl border transition-all duration-300 ${
        isCompleted 
          ? "bg-green-500/10 border-green-500/30" 
          : `${style.bg} ${style.border} hover:border-opacity-80`
      }`}
    >
      {/* Gradient accent */}
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${style.color}`} />
      
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`text-3xl flex-shrink-0 ${isCompleted ? "grayscale" : ""}`}>
            {challenge.icon}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-rajdhani uppercase tracking-wider ${style.text}`}>
                {CATEGORY_NAMES[challenge.category]}
              </span>
              <div className="flex items-center gap-1 text-neon-yellow text-xs">
                <Zap className="w-3 h-3" />
                <span className="font-bold">{challenge.xp} XP</span>
              </div>
              {hasInteractiveActivity && !isCompleted && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/30">
                  INTERACTIVO
                </span>
              )}
            </div>
            
            <h4 className={`font-rajdhani font-bold text-lg mb-1 ${isCompleted ? "text-green-400 line-through" : "text-white"}`}>
              {challenge.title}
            </h4>
            
            <p className="text-sm text-slate-400 line-clamp-2">
              {challenge.description}
            </p>
          </div>
          
          {/* Action Button */}
          <div className="flex-shrink-0">
            {isCompleted ? (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center"
              >
                <CheckCircle2 className="w-6 h-6 text-green-400" />
              </motion.div>
            ) : hasInteractiveActivity ? (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onStartActivity(challenge.id)}
                disabled={completing === challenge.id}
                className="w-10 h-10 rounded-full bg-neon-cyan/20 border border-neon-cyan/50 flex items-center justify-center transition-all hover:bg-neon-cyan/30"
                data-testid={`start-activity-${challenge.id}`}
              >
                {completing === challenge.id ? (
                  <div className="w-5 h-5 border-2 border-neon-cyan/30 border-t-neon-cyan rounded-full animate-spin" />
                ) : (
                  <Play className="w-5 h-5 text-neon-cyan" fill="currentColor" />
                )}
              </motion.button>
            ) : (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onComplete(challenge.id)}
                disabled={completing === challenge.id}
                className={`w-10 h-10 rounded-full ${style.bg} ${style.border} border flex items-center justify-center transition-all hover:bg-white/10`}
                data-testid={`complete-challenge-${challenge.id}`}
              >
                {completing === challenge.id ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Circle className={`w-5 h-5 ${style.text}`} />
                )}
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Leaderboard Component
const Leaderboard = ({ onClose }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("week");

  useEffect(() => {
    fetchLeaderboard();
  }, [period]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/challenges/leaderboard?period=${period}`);
      setLeaderboard(response.data.leaderboard);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-void-paper border border-neon-purple/30 rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-orbitron text-xl text-white flex items-center gap-2">
            <Crown className="w-5 h-5 text-neon-yellow" />
            Ranking
          </h3>
          <div className="flex gap-2">
            <button
              onClick={() => setPeriod("week")}
              className={`px-3 py-1 rounded-lg text-xs font-rajdhani transition-all ${
                period === "week" 
                  ? "bg-neon-purple/30 text-neon-purple border border-neon-purple/50" 
                  : "bg-white/5 text-slate-400 border border-white/10"
              }`}
            >
              Semana
            </button>
            <button
              onClick={() => setPeriod("month")}
              className={`px-3 py-1 rounded-lg text-xs font-rajdhani transition-all ${
                period === "month" 
                  ? "bg-neon-purple/30 text-neon-purple border border-neon-purple/50" 
                  : "bg-white/5 text-slate-400 border border-white/10"
              }`}
            >
              Mes
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-14 bg-white/5 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center text-slate-400 py-8">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Aún no hay datos para este período</p>
            </div>
          ) : (
            leaderboard.map((entry, i) => (
              <motion.div
                key={entry.user_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className={`flex items-center gap-3 p-3 rounded-lg ${
                  entry.is_current_user 
                    ? "bg-neon-cyan/10 border border-neon-cyan/30" 
                    : "bg-white/5 border border-white/10"
                }`}
              >
                {/* Rank */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-orbitron text-sm ${
                  entry.rank === 1 ? "bg-yellow-500/30 text-yellow-400" :
                  entry.rank === 2 ? "bg-slate-400/30 text-slate-300" :
                  entry.rank === 3 ? "bg-orange-600/30 text-orange-400" :
                  "bg-white/10 text-slate-400"
                }`}>
                  {entry.rank <= 3 ? ["🥇", "🥈", "🥉"][entry.rank - 1] : entry.rank}
                </div>
                
                {/* User */}
                <div className="flex-1 min-w-0">
                  <p className={`font-rajdhani font-semibold truncate ${entry.is_current_user ? "text-neon-cyan" : "text-white"}`}>
                    {entry.name}
                    {entry.is_current_user && <span className="text-xs ml-1">(Tú)</span>}
                  </p>
                  <p className="text-xs text-slate-500">{entry.challenges_completed} retos</p>
                </div>
                
                {/* XP */}
                <div className="flex items-center gap-1 text-neon-yellow">
                  <Zap className="w-4 h-4" />
                  <span className="font-bold">{entry.total_xp}</span>
                </div>
              </motion.div>
            ))
          )}
        </div>

        <button
          onClick={onClose}
          className="mt-4 w-full py-2 rounded-lg bg-white/10 text-slate-300 font-rajdhani hover:bg-white/20 transition-colors"
        >
          Cerrar
        </button>
      </motion.div>
    </motion.div>
  );
};

// Main Component
export const DailyChallenges = ({ compact = false }) => {
  const { authMode } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [history, setHistory] = useState(null);
  const [activeActivity, setActiveActivity] = useState(null); // For interactive challenges

  useEffect(() => {
    fetchChallenges();
    if (authMode !== "incognito") {
      fetchHistory();
    }
  }, [authMode]);

  const fetchChallenges = async () => {
    try {
      const response = await axios.get(`${API}/challenges/daily`);
      setChallenges(response.data.challenges);
    } catch (error) {
      console.error("Error fetching challenges:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/challenges/history?days=7`);
      setHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };

  const handleComplete = async (challengeId) => {
    if (authMode === "incognito") {
      toast.error("Regístrate para completar retos y ganar XP");
      return;
    }

    setCompleting(challengeId);
    try {
      const response = await axios.post(`${API}/challenges/complete`, {
        challenge_id: challengeId
      });
      
      toast.success(response.data.message, {
        icon: "🎯"
      });
      
      // Update local state
      setChallenges(prev => prev.map(c => 
        c.id === challengeId ? { ...c, completed: true } : c
      ));
      
      // Refresh history
      fetchHistory();
    } catch (error) {
      const message = error.response?.data?.detail || "Error al completar el reto";
      toast.error(message);
    } finally {
      setCompleting(null);
    }
  };

  // Start an interactive activity
  const handleStartActivity = (challengeId) => {
    if (authMode === "incognito") {
      toast.error("Regístrate para completar retos y ganar XP");
      return;
    }
    setActiveActivity(challengeId);
  };

  // When activity is completed, mark the challenge as done
  const handleActivityComplete = async () => {
    if (activeActivity) {
      await handleComplete(activeActivity);
      setActiveActivity(null);
    }
  };

  const completedCount = challenges.filter(c => c.completed).length;
  const totalXP = challenges.filter(c => c.completed).reduce((sum, c) => sum + c.xp, 0);

  if (loading) {
    return (
      <GlassCard className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-white/10 rounded w-1/3" />
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-white/10 rounded-xl" />
            ))}
          </div>
        </div>
      </GlassCard>
    );
  }

  // Compact version for dashboard
  if (compact) {
    return (
      <GlassCard className="p-4" data-testid="daily-challenges-compact">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-orbitron text-base text-white flex items-center gap-2">
            <Target className="w-4 h-4 text-neon-cyan" />
            Retos del Día
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-neon-cyan font-rajdhani">
              {completedCount}/{challenges.length}
            </span>
            <button
              onClick={() => setShowLeaderboard(true)}
              className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
              data-testid="show-leaderboard-btn"
            >
              <Crown className="w-4 h-4 text-neon-yellow" />
            </button>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="h-2 bg-black/30 rounded-full overflow-hidden mb-3">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${(completedCount / challenges.length) * 100}%` }}
            className="h-full bg-gradient-to-r from-neon-cyan to-neon-purple"
          />
        </div>
        
        {/* Mini challenge list */}
        <div className="space-y-2">
          {challenges.map(challenge => {
            const hasInteractiveActivity = INTERACTIVE_CHALLENGES.includes(challenge.id);
            return (
              <div
                key={challenge.id}
                className={`flex items-center gap-2 p-2 rounded-lg transition-all ${
                  challenge.completed 
                    ? "bg-green-500/10 border border-green-500/20" 
                    : "bg-white/5 border border-white/10"
                }`}
              >
                <span className="text-xl">{challenge.icon}</span>
                <span className={`flex-1 text-sm font-rajdhani ${challenge.completed ? "text-green-400 line-through" : "text-slate-300"}`}>
                  {challenge.title}
                </span>
                {challenge.completed ? (
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                ) : hasInteractiveActivity ? (
                  <button
                    onClick={() => handleStartActivity(challenge.id)}
                    disabled={completing === challenge.id}
                    className="p-1 rounded bg-neon-cyan/20 hover:bg-neon-cyan/30 transition-colors"
                  >
                    {completing === challenge.id ? (
                      <div className="w-4 h-4 border-2 border-neon-cyan/30 border-t-neon-cyan rounded-full animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 text-neon-cyan" fill="currentColor" />
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => handleComplete(challenge.id)}
                    disabled={completing === challenge.id}
                    className="p-1 rounded bg-neon-cyan/20 hover:bg-neon-cyan/30 transition-colors"
                  >
                    {completing === challenge.id ? (
                      <div className="w-4 h-4 border-2 border-neon-cyan/30 border-t-neon-cyan rounded-full animate-spin" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-neon-cyan" />
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <AnimatePresence>
          {showLeaderboard && <Leaderboard onClose={() => setShowLeaderboard(false)} />}
        </AnimatePresence>

        {/* Interactive Challenge Activity Modal */}
        {activeActivity && (
          <ChallengeActivity
            challengeId={activeActivity}
            onComplete={handleActivityComplete}
            onClose={() => setActiveActivity(null)}
          />
        )}
      </GlassCard>
    );
  }

  // Full version
  return (
    <>
      <GlassCard className="p-6" data-testid="daily-challenges-full">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
              <Target className="w-6 h-6 text-neon-cyan" />
              Retos Diarios de Bienestar
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              Completa retos para ganar XP y mejorar tu bienestar
            </p>
          </div>
          <button
            onClick={() => setShowLeaderboard(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-neon-yellow/10 border border-neon-yellow/30 text-neon-yellow hover:bg-neon-yellow/20 transition-colors"
            data-testid="show-leaderboard-btn-full"
          >
            <Crown className="w-4 h-4" />
            <span className="font-rajdhani text-sm">Ranking</span>
          </button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white/5 rounded-lg p-3 text-center border border-white/10">
            <div className="flex items-center justify-center gap-1 text-neon-cyan mb-1">
              <CheckCircle2 className="w-4 h-4" />
              <span className="font-orbitron text-xl">{completedCount}/{challenges.length}</span>
            </div>
            <span className="text-xs text-slate-400 font-rajdhani">Completados</span>
          </div>
          <div className="bg-white/5 rounded-lg p-3 text-center border border-white/10">
            <div className="flex items-center justify-center gap-1 text-neon-yellow mb-1">
              <Zap className="w-4 h-4" />
              <span className="font-orbitron text-xl">{totalXP}</span>
            </div>
            <span className="text-xs text-slate-400 font-rajdhani">XP Hoy</span>
          </div>
          <div className="bg-white/5 rounded-lg p-3 text-center border border-white/10">
            <div className="flex items-center justify-center gap-1 text-neon-purple mb-1">
              <Calendar className="w-4 h-4" />
              <span className="font-orbitron text-xl">{history?.days_active || 0}</span>
            </div>
            <span className="text-xs text-slate-400 font-rajdhani">Días Activos</span>
          </div>
        </div>

        {/* Challenges Grid */}
        <div className="space-y-3">
          {challenges.map((challenge, index) => (
            <ChallengeCard
              key={challenge.id}
              challenge={challenge}
              onComplete={handleComplete}
              onStartActivity={handleStartActivity}
              completing={completing}
            />
          ))}
        </div>

        {/* Completion celebration */}
        {completedCount === challenges.length && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-4 rounded-xl bg-gradient-to-r from-neon-cyan/20 to-neon-purple/20 border border-neon-cyan/30 text-center"
          >
            <Sparkles className="w-8 h-8 mx-auto text-neon-yellow mb-2" />
            <h3 className="font-orbitron text-lg text-white mb-1">
              ¡Todos los retos completados!
            </h3>
            <p className="text-sm text-slate-300">
              Has ganado <span className="text-neon-yellow font-bold">{totalXP} XP</span> hoy. ¡Increíble!
            </p>
          </motion.div>
        )}
      </GlassCard>

      <AnimatePresence>
        {showLeaderboard && <Leaderboard onClose={() => setShowLeaderboard(false)} />}
      </AnimatePresence>

      {/* Interactive Challenge Activity Modal */}
      {activeActivity && (
        <ChallengeActivity
          challengeId={activeActivity}
          onComplete={handleActivityComplete}
          onClose={() => setActiveActivity(null)}
        />
      )}
    </>
  );
};

export default DailyChallenges;
